import React from 'react';
import { css } from '@emotion/react';
import { useStripe } from '@stripe/react-stripe-js';

import Text from '$/components/ui/Text';
import Modal from '$/components/ui/Modal';
import Button from '$/components/ui/Button';
import { ErrorMessage } from '$/components/ui/MessageText';
import { StripeLoader, withStripeLoader } from '$/components/StripeLoader';
import { UpdateBillingInfoModal } from '$/components/UpdateBillingInfoModal';

import {
  MyRole,
  SubscriptionStatus,
  useRestoreSubscriptionMutation,
} from '$/GraphQL/schema/schema';
import { useMe } from '$/hooks/useMe';
import { useOnSubscriptionChanged } from '$/hooks/useOnSubscriptionChanged';

interface UpdateBillingModalState {
  isOpen: boolean;
  fresh: boolean;
  isRestoring: boolean;
}

const RestoreAccountModal: React.FC<{
  onClose: () => void;
}> = withStripeLoader(({ onClose }) => {
  const me = useMe();
  const stripe = useStripe();
  const team = me.team;
  const onSubscriptionChanged = useOnSubscriptionChanged();
  const [error, setError] = React.useState<string | undefined>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [modalState, updateModalState] = React.useState<UpdateBillingModalState>({
    isOpen: false,
    fresh: false,
    isRestoring: false,
  });
  const [restoreSubscriptionMutation] = useRestoreSubscriptionMutation();

  const hasPermission = React.useCallback((): boolean => {
    if (![MyRole.Admin, MyRole.Ops].includes(me.inTeam.role)) {
      setError('Cannot reactivate subscription. Only Admins can reactivate subscription.');
      return false;
    }
    return true;
  }, [me.inTeam.role]);

  const closeModal = React.useCallback((): void => {
    updateModalState((state) => ({ ...state, isOpen: false }));
  }, []);

  const onRestore = React.useCallback(
    async (fresh: boolean, paymentIntentId?: string): Promise<void> => {
      if (hasPermission()) {
        setLoading(true);
        updateModalState({ isOpen: false, isRestoring: true, fresh });
        try {
          const result = await restoreSubscriptionMutation({
            variables: {
              fresh,
              paymentIntentId,
            },
          });

          if (result.data?.restoreSubscription?.paymentIntentSecret) {
            const confirmResult = await stripe?.confirmCardPayment(
              result.data?.restoreSubscription?.paymentIntentSecret
            );
            if (confirmResult?.error) {
              throw confirmResult.error;
            }
            if (confirmResult?.paymentIntent?.id) {
              await onRestore(fresh, confirmResult.paymentIntent.id);
            }
          } else if (result.data?.restoreSubscription?.team.id) {
            await onSubscriptionChanged();
            onClose();
          }
        } catch {
          updateModalState({ isOpen: true, isRestoring: true, fresh });
        } finally {
          setLoading(false);
        }
      }
    },
    [onClose, hasPermission, onSubscriptionChanged, restoreSubscriptionMutation, stripe]
  );

  const onStartFresh = React.useCallback((): void => {
    onRestore(true);
  }, [onRestore]);

  const onRestoreAll = React.useCallback((): void => {
    onRestore(false);
  }, [onRestore]);

  const onUpdatePayment = React.useCallback((): void => {
    updateModalState({ isOpen: true, isRestoring: false, fresh: false });
  }, []);

  const subStatus = team?.subscriptionStatus;

  return (
    <StripeLoader>
      {modalState.isOpen ? (
        <UpdateBillingInfoModal
          isRestoring={modalState.isRestoring}
          fresh={modalState.fresh}
          toggleModal={closeModal}
        />
      ) : (
        <>
          <Modal>
            <Modal.Header gray>
              <Modal.Title>Would you like to reactivate your subscription?</Modal.Title>
              <Modal.Close onClick={onClose} />
            </Modal.Header>
            <Modal.Body>
              {[SubscriptionStatus.Incomplete, SubscriptionStatus.PastDue].includes(subStatus) ? (
                <Text.body>
                  This credit card could not be charged. Please update your payment information.
                  Your new credit card will be charged immediately
                  <p />
                  If this problem persists, please contact{' '}
                  <a href='mailto:support@ewebinar.com'>support@ewebinar.com</a>.
                </Text.body>
              ) : (
                <>
                  <Text.body>
                    <p>
                      You can restore your account and publish all eWebinars that were previously
                      active, or start fresh and all your eWebinars will be put into Drafts. Your
                      credit card on file will be billed immediately to reactivate this account.
                    </p>
                    <p>Your credit card on file will be charged immediately.</p>
                  </Text.body>
                </>
              )}

              <ErrorMessage
                error={error}
                css={css`
                  justify-content: flex-start !important;
                `}
              />
            </Modal.Body>
            <Modal.Footer className='flex justify-between items-center'>
              {[SubscriptionStatus.Incomplete, SubscriptionStatus.PastDue].includes(subStatus) ? (
                <>
                  <div></div>
                  <Button.rounded onClick={onUpdatePayment}>
                    Update payment information
                  </Button.rounded>
                </>
              ) : (
                <>
                  <Button.submit
                    disabled={loading}
                    submitting={modalState.fresh && loading}
                    onClick={onStartFresh}
                    className='mr-1e'
                    button={Button.bordered}
                  >
                    Start fresh
                  </Button.submit>

                  <Button.submit
                    disabled={loading}
                    submitting={!modalState.fresh && loading}
                    onClick={onRestoreAll}
                  >
                    Restore all eWebinars
                  </Button.submit>
                </>
              )}
            </Modal.Footer>
          </Modal>
        </>
      )}
    </StripeLoader>
  );
});

export default RestoreAccountModal;
